import { useCallback, useState } from 'react';

// Returns true if a row has any meaningful content (not completely blank).
export function isRowNonEmpty(item) {
  if (!item) return false;
  if (item.product_id) return true;
  if ((item.description || '').trim()) return true;
  if (item.supplier_id) return true;
  if (parseFloat(item.unit_cost) > 0) return true;
  if (parseFloat(item.unit_price) > 0) return true;
  if ((item.unit_cost_expr || '').trim()) return true;
  if ((item.unit_price_expr || '').trim()) return true;
  if (parseFloat(item.quantity) !== 1 && item.quantity !== '' && item.quantity !== undefined) return true;
  if ((item.costcurr || '').trim()) return true;
  return false;
}

// A hash of item rows and invoice currency. Includes all non-empty rows.
export function computeOfferHash(items, invCurrency, discount, fxFetchedAt) {
  const relevantData = (items || [])
    .filter(item => isRowNonEmpty(item))
    .map(item => ({
      product_id: item.product_id,
      supplier_id: item.supplier_id,
      quantity: item.quantity,
      unit_cost: item.unit_cost,
      unit_cost_expr: item.unit_cost_expr,
      unit_price: item.unit_price,
      unit_price_expr: item.unit_price_expr,
      markup_percent: item.markup_percent,
      used_rate_markup: item.used_rate_markup,
      hidden: item.hidden,
      vat: item.vat,
      total_vat_adjustment_expr: item.total_vat_adjustment_expr,
      total_price_vat_expr: item.total_price_vat_expr,
      description: item.description,
      costcurr: item.costcurr,
    }));

  const dataToHash = { items: relevantData, invCurrency, discount: discount || 0, fxFetchedAt: fxFetchedAt || '' };
  const json = JSON.stringify(dataToHash);

  // Hash function (djb2 algorithm). Convert to 32 bit int
  let hash = 5381;
  for (let i = 0; i < json.length; i++) {
    hash = ((hash << 5) + hash) + json.charCodeAt(i);
    hash = hash & hash;
  }
  return hash.toString(16);
}

export function useOfferState({ setInvoiceCurrency, setCurrencyRates } = {}) {
  const parseNumericField = useCallback((value) => {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed === '') return '';
    if (/^\d+(?:[.,]\d+)?$/.test(trimmed)) {
      const normalized = trimmed.replace(',', '.');
      const num = Number(normalized);
      return Number.isNaN(num) ? value : num;
    }
    return value;
  }, []);

  const emptyItem = useCallback((id) => ({
    id,
    product: '',
    product_id: null,
    supplier_name: '',
    supplier_id: null,
    description: '',
    quantity: 1,
    unit_cost: '',
    unit_cost_expr: '',
    total_cost: 0,
    markup_percent: 0,
    unit_price: '',
    unit_price_expr: '',
    unit_price_locked: false,
    total_price: 0,
    local_cost: 0,
    costcurr: '',
    used_rate_markup: null,
    hidden: false,
    product_hidden: false,
    profit_percent: 0,
    vat: 0,
    vat_price: 0,
    total_price_vat: 0,
    total_price_vat_expr: '',
    total_vat_adjustment_expr: '',
    total_vat_adjustment_value: 0,
    total: 0,
    zoho_line_item_id: null
  }), []);

  const buildEmptyOffer = useCallback(() => ({
    id: null,
    quote_number: '',
    client_name: '',
    client_email: '',
    client_id: null,
    deal_id: null,
    deal_name: '',
    date: new Date().toISOString().split('T')[0],
    items: [emptyItem(1)],
    subtotal: 0,
    tax: 0,
    total: 0,
    discount: 0,
    status: 'New offer'
  }), [emptyItem]);

  const [offer, setOffer] = useState(buildEmptyOffer);

  const normalizeLoadedOffer = useCallback((loaded) => {
    const base = buildEmptyOffer();
    const items = Array.isArray(loaded?.items) ? loaded.items : [];
    const normalizedItems = items
      .map((item, idx) => {
        // Preserve empty rows in-place so their position survives save/reload
        if (!isRowNonEmpty(item)) {
          return emptyItem(item?.id ?? (idx + 1));
        }
        const id = item?.id ?? (idx + 1);
        const normalizedItem = { ...emptyItem(id), ...item, id };
        normalizedItem.unit_cost_expr = normalizedItem.unit_cost_expr ?? '';
        normalizedItem.unit_price_expr = normalizedItem.unit_price_expr ?? '';
        normalizedItem.total_price_vat_expr = normalizedItem.total_price_vat_expr ?? '';
        normalizedItem.total_vat_adjustment_expr = normalizedItem.total_vat_adjustment_expr ?? '';
        normalizedItem.total_vat_adjustment_value = parseNumericField(normalizedItem.total_vat_adjustment_value ?? 0);

        // Backward compat: if there's a numeric value but no expr, convert value to expr
        if (!normalizedItem.total_vat_adjustment_expr && normalizedItem.total_vat_adjustment_value) {
          const val = parseFloat(normalizedItem.total_vat_adjustment_value) || 0;
          if (Math.abs(val) > 0.005) {
            const sign = val > 0 ? '+' : '';
            normalizedItem.total_vat_adjustment_expr = `${sign}${val}`;
          }
        }

        const unitCostExpr = parseNumericField(normalizedItem.unit_cost_expr);
        const unitPriceExpr = parseNumericField(normalizedItem.unit_price_expr);

        normalizedItem.unit_cost = parseNumericField(normalizedItem.unit_cost);
        normalizedItem.unit_price = parseNumericField(normalizedItem.unit_price);
        normalizedItem.quantity = parseNumericField(normalizedItem.quantity);
        normalizedItem.markup_percent = parseNumericField(normalizedItem.markup_percent);
        normalizedItem.used_rate_markup = parseNumericField(normalizedItem.used_rate_markup);
        normalizedItem.hidden = !!normalizedItem.hidden;
        normalizedItem.vat = parseNumericField(normalizedItem.vat);

        if ((normalizedItem.unit_cost === '' || Number.isNaN(Number(normalizedItem.unit_cost))) && typeof unitCostExpr === 'number') {
          normalizedItem.unit_cost = unitCostExpr;
        }
        if ((normalizedItem.unit_price === '' || Number.isNaN(Number(normalizedItem.unit_price))) && typeof unitPriceExpr === 'number') {
          normalizedItem.unit_price = unitPriceExpr;
        }

        return normalizedItem;
      });
    if (normalizedItems.length === 0) {
      normalizedItems.push(emptyItem(1));
    }
    if (loaded?.inv_currency && setInvoiceCurrency) {
      setInvoiceCurrency(loaded.inv_currency);
    }
    if (loaded?.fx_rates && Object.keys(loaded.fx_rates).length > 0 && setCurrencyRates) {
      console.log('[FX-DEBUG] normalizeLoadedOffer: setCurrencyRates called. EUR:', loaded.fx_rates.EUR, ', keys:', Object.keys(loaded.fx_rates).length);
      setCurrencyRates(loaded.fx_rates);
    }
    const result = { ...base, ...loaded, items: normalizedItems };
    console.log('[FX-DEBUG] normalizeLoadedOffer returning offer. fx_rates EUR:', result.fx_rates?.EUR, ', fx_fetched_at:', result.fx_fetched_at);
    return result;
  }, [buildEmptyOffer, emptyItem, parseNumericField, setCurrencyRates, setInvoiceCurrency]);

  return {
    offer,
    setOffer,
    emptyItem,
    buildEmptyOffer,
    normalizeLoadedOffer
  };
}